import React, { useCallback, useRef, useState } from 'react';
import { DialogTitle, IconButton, Typography, DialogActions, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MonacoEditor, MonacoEditorHandle } from '@warehouse/shared/ui';
import Dialog from '../../../../../src/components/library/Dialog';
import ButtonComponent from '../../../../../src/components/library/Button';
import Loader from '../../../../../src/components/library/Loader';

const StyledDialogTitle = styled(DialogTitle)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	paddingBottom: '0 !important',
});

const StyledDialog = styled(Dialog)({
	'& .MuiDialog-paper': {
		height: '80vh',
		display: 'flex',
		flexDirection: 'column',
	},
});

const StyledDialogFooter = styled(DialogActions)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	padding: '16px 24px',
	gap: '8px',
});

type MonacoEditorDialogProps = {
	open: boolean;
	onClose: () => void;
	onChange?: (mec: string) => void;
	onSaveHandler?: (mec: string) => void;
	content: string | undefined;
	title: string;
	subtitle?: string;
	language?: string;
	errors?: string[];
	loading?: boolean;
	isSaveButtonEnabled?: boolean;
	editModeAvailability?: boolean;
	showDownloadButton?: boolean;
};

export function MonacoEditorDialog({
	open,
	onClose,
	onChange,
	onSaveHandler,
	content,
	title,
	subtitle,
	language = 'xml',
	errors = [],
	loading = false,
	isSaveButtonEnabled = true,
	editModeAvailability = true,
	showDownloadButton = false,
}: MonacoEditorDialogProps) {
	const editorRef = useRef<MonacoEditorHandle>(null);
	const [isDirty, setIsDirty] = useState(false);

	const handleSave = useCallback(() => {
		const value = editorRef.current?.getValue();
		if (value !== undefined && onSaveHandler) {
			onSaveHandler(value);
		}
	}, [onSaveHandler]);

	const handleCancel = useCallback(() => {
		onClose();
	}, [onClose]);

	return (
		<StyledDialog open={open} onClose={onClose} fullWidth maxWidth="xl">
			<StyledDialogTitle>
				<div>
					<Typography variant="h4">{title}</Typography>
					{subtitle && (
						<Typography variant="h6" color="text.secondary">
							{subtitle}
						</Typography>
					)}
				</div>
				<IconButton onClick={onClose} aria-label="Close">
					<CloseIcon />
				</IconButton>
			</StyledDialogTitle>

			<MonacoEditor
				ref={editorRef}
				content={content}
				language={language}
				errors={errors}
				editModeAvailability={editModeAvailability}
				showDownloadButton={showDownloadButton}
				onChange={onChange}
				onDirtyChange={setIsDirty}
				title={title}
			/>

			{onSaveHandler && (
				<StyledDialogFooter>
					{isDirty && (
						<Typography variant="body2" color="error.main" sx={{ marginRight: 'auto' }}>
							You have unsaved changes
						</Typography>
					)}
					<ButtonComponent
						style={{
							margin: 0,
							padding: 15,
							height: 40,
							width: 100,
						}}
						nxstyle="secondary-red"
						onClick={handleCancel}
					>
						Cancel
					</ButtonComponent>
					<ButtonComponent
						style={{
							margin: 0,
							padding: 15,
							height: 40,
							width: 100,
						}}
						nxstyle="primary-blue"
						onClick={handleSave}
						disabled={!isDirty || loading || !isSaveButtonEnabled}
						endIcon={loading ? <Loader size={22} color="#fff" /> : null}
					>
						{loading ? 'Saving' : 'Save'}
					</ButtonComponent>
				</StyledDialogFooter>
			)}
		</StyledDialog>
	);
}
